import React, { useState, useEffect, useCallback, useContext } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Section1 from './Section1';
import Section2 from './Section2';
import Section3 from './Section3';
import { useSpinner } from '../../spinner/hooks/useSpinner';
import { CmsPageContext } from '../../cmsPage/definitions';

// Custom section components for DirectoryList
const renderSection1 = (entry) => <Section1 entry={entry} />;

const renderSection2 = (entry) => <Section2 entry={entry} />;

const renderSection3 = (entry) => <Section3 entry={entry} />;

const DirectoryList = ({
  pageTitle,
  hideCategories,
  pluralEntriesTitle,
  singleCategoryTitle,
  pluralCategoriesTitle,
  singleEntryTitle,
  moduleColor,
  categories,
  entryListArray,
  directoryListSection1Component,
  directoryListSection2Component,
  directoryListSection3Component,
  entriesOrderBy,
  onEditEntry,
  onManageCategories,
  onEntrySave,
  updateCategories,
  errorText,
  loading,
}) => {
  const { spinnerProvider } = useContext(CmsPageContext);
  useSpinner(spinnerProvider, loading);
  const sectionComponent1 = directoryListSection1Component || renderSection1;
  const sectionComponent2 = directoryListSection2Component || renderSection2;
  const sectionComponent3 = directoryListSection3Component || renderSection3;
  // State management
  const [seperateCategories, setSeperateCategories] = useState({ checked: true });
  const [searchText, setSearchText] = useState('');
  const [draggables, setDraggables] = useState({
    category: { orderingMode: true, selected: '', held: '' },
    entry: { orderingMode: false, selected: '', held: '' },
  });
  const [reordered, setReordered] = useState(false);

  // Local state for drag and drop operations
  const [localEntryArraysByCategory, setLocalEntryArraysByCategory] = useState([]);
  const [localEntryListArray, setLocalEntryListArray] = useState([]);

  // Initialize local state from props
  useEffect(() => {
    if (!reordered) {
      setLocalEntryArraysByCategory(categories || []);
      setLocalEntryListArray(entryListArray || []);
    }
  }, [JSON.stringify(categories), JSON.stringify(entryListArray), reordered]);

  // Handle checkbox change for "Separate by Category"
  const handleSeperateCategories = (e) => {
    setSeperateCategories({ checked: e.target.checked });
  };

  // Filter entries based on search
  const filterBySearch = (item) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return item.title.toLowerCase().includes(searchLower);
  };

  const sortEntries = useCallback(
    (a, b) => {
      if (entriesOrderBy && typeof entriesOrderBy === 'string') {
        return a[entriesOrderBy] > b[entriesOrderBy] ? 1 : -1;
      }
      return 0;
    },
    [entriesOrderBy]
  );

  // Drag and drop handlers
  const onDragEnd = (result) => {
    const { source, destination, type } = result;

    // Dropped outside the list
    if (!destination) return;
    // Entry reordering within category
    else if (
      type === 'entryType' &&
      seperateCategories.checked && // If reordering in the same category
      source.droppableId === destination.droppableId
    ) {
      const categoryId = source.droppableId;
      const categoryIndex = localEntryArraysByCategory.findIndex(
        (c) => c.id.toString() === categoryId
      );

      if (categoryIndex !== -1) {
        const sourceIndex = source.index;
        const destinationIndex = destination.index;
        const category = localEntryArraysByCategory[categoryIndex];
        const categoryItems = Object.values(category.items).sort(sortEntries);
        const sourceItem = categoryItems[sourceIndex];
        sourceItem.indexOrder = destinationIndex;
        const updatedItems = categoryItems
          .filter((item) => item.id !== sourceItem.id)
          .map((item, index) => {
            const indexOrder = index >= destinationIndex ? index + 1 : index;
            return {
              ...item,
              indexOrder,
            };
          });
        updatedItems.push(sourceItem);
        const itemObject = updatedItems.reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {});
        const updatedCategory = {
          ...category,
          items: itemObject,
        };
        const newCategoryList = [...localEntryArraysByCategory];
        newCategoryList[categoryIndex] = updatedCategory;
        setLocalEntryArraysByCategory(newCategoryList);
        setReordered(true);
      }
    }
  };

  // Save changes after reordering
  const updateList = () => {
    updateCategories(localEntryArraysByCategory);
    setReordered(false);
  };

  const cancelReorder = () => {
    setLocalEntryArraysByCategory(categories || []);
    setReordered(false);
  };

  const handleEditEntry = (categoryId, entryId, copy) => {
    onEditEntry(categoryId, entryId, copy);
  };

  const handleAddEntry = () => {
    onEntrySave();
  };

  return (
    <div>
      <div className="header row">
        <h1 className="page-title">{pageTitle}</h1>
        <div
          className="page-filter no-left-padding top-margin-10"
          style={{ top: '25px', display: hideCategories ? 'none' : 'flex' }}
        >
          <div className="row">
            <div className="col-xs-12">
              <div className="checkbox">
                <label>
                  <input
                    disabled={reordered}
                    checked={seperateCategories.checked}
                    onChange={handleSeperateCategories}
                    type="checkbox"
                  />
                  &nbsp; Separate {pluralEntriesTitle} By {singleCategoryTitle}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`module ${moduleColor} row`}>
        <div className="module-header">
          <span className="module-title subtitle">
            {pluralEntriesTitle}
            {seperateCategories.checked && <span> By {singleCategoryTitle}</span>}
          </span>
        </div>
        <div className="col-xs-12">
          <div className="module-body">
            <div id="directoryListGrowl">{errorText}</div>
            <form name={`${singleEntryTitle}List`} role="form">
              {/* Category ordering mode controls */}
              {reordered && (
                <div className="row padding-bottom border-bottom">
                  <div className="text-center">
                    <button
                      style={{ marginRight: '6px' }}
                      type="button"
                      className="btn"
                      onClick={cancelReorder}
                    >
                      Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={updateList}>
                      Save Order
                    </button>
                  </div>
                </div>
              )}

              {/* Search and action buttons */}
              <div className="row" style={{ display: reordered ? 'none' : 'flex' }}>
                <div className="form-inline no-left-padding">
                  <span className="header-input">
                    <input
                      className="form-control"
                      placeholder="Search"
                      autoFocus
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </span>
                  <span className="pull-right">
                    <button
                      className={localEntryArraysByCategory.length ? 'btn' : 'btn disabled'}
                      title={
                        localEntryArraysByCategory.length
                          ? ''
                          : `Must Add ${singleCategoryTitle} First`
                      }
                      onClick={() => (localEntryArraysByCategory.length ? handleAddEntry() : null)}
                      type="button"
                    >
                      Add {singleEntryTitle}
                    </button>
                    {!hideCategories && (
                      <button
                        onClick={() => onManageCategories && onManageCategories()}
                        type="button"
                        className="btn"
                      >
                        Manage {pluralCategoriesTitle}
                      </button>
                    )}
                  </span>
                </div>
              </div>

              {/* Entries without separating by category and hide categories */}
              {!seperateCategories.checked && hideCategories && (
                <ul className="directory-info-list">
                  {localEntryListArray.sort(sortEntries).map((entry, index) => {
                    return (
                      <li
                        key={entry.id}
                        className={`directory-info row padding-top border-top ${
                          index === localEntryListArray.length - 1
                            ? 'border-bottom padding-bottom'
                            : ''
                        }`}
                      >
                        {sectionComponent1(entry)}
                        {sectionComponent2(entry)}
                        {sectionComponent3(entry)}
                        <div className="col-xs-2 text-right no-right-padding pull-right">
                          <button
                            disabled={true}
                            onClick={() => handleEditEntry(entry.categoryId, entry.id)}
                            type="button"
                            className="btn"
                            style={{ marginRight: '6px' }}
                          >
                            Edit
                          </button>
                          <button
                            disabled={true}
                            onClick={() =>
                              localEntryArraysByCategory.length ? handleAddEntry() : null
                            }
                            type="button"
                            className="btn"
                          >
                            Copy
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Entries without separating by category and with categories */}
              {!seperateCategories.checked && !hideCategories && (
                <div>
                  {localEntryListArray.sort(sortEntries).map((entry, index) => (
                    <div
                      key={entry.id}
                      className={`row equal-height-cols padding-top border-top ${
                        index === localEntryListArray.length - 1
                          ? 'border-bottom padding-bottom'
                          : ''
                      }`}
                    >
                      {sectionComponent1(entry)}
                      {sectionComponent2(entry)}
                      {sectionComponent3(entry)}
                      <div className="col-xs-2 text-right no-right-padding pull-right">
                        <button
                          disabled={true}
                          onClick={() => handleEditEntry(entry.categoryId, entry.id)}
                          type="button"
                          className="btn"
                          style={{ marginRight: '6px' }}
                        >
                          Edit
                        </button>
                        <button
                          disabled={true}
                          onClick={() =>
                            localEntryArraysByCategory.length ? handleAddEntry() : null
                          }
                          type="button"
                          className="btn"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Entries with category separation */}
              {seperateCategories.checked && (
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="categoryListDrop" type="categoryType">
                    {(provided) => (
                      <ul
                        //category list
                        key="categoryListDrop"
                        className="directory-category-list"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {localEntryArraysByCategory.filter(filterBySearch).map((category) => (
                          <li
                            //category
                            key={category.id}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`row ${
                              draggables.category.selected === category.id ? 'selected' : ''
                            } ${draggables.category.held === category.id ? 'held' : ''} ${
                              searchText ? 'dragDisabled' : ''
                            } ${draggables.category.orderingMode ? 'ordering-mode' : ''}`}
                          >
                            <div className="no-left-padding">
                              <div className="col-xs-12">
                                <div className="row">
                                  <div className="module-sub-header">{category.title}</div>
                                </div>

                                <Droppable
                                  droppableId={category?.id || category?.id?.toString()}
                                  type="entryType"
                                >
                                  {(provided) => (
                                    <ul
                                      //entry list
                                      className="directory-info-list"
                                      ref={provided.innerRef}
                                      {...provided.droppableProps}
                                    >
                                      {(_.isArray(category.items)
                                        ? category.items
                                        : Object.values(category.items)
                                      )
                                        .sort(sortEntries)
                                        .map((entry, entryIndex) => (
                                          <Draggable
                                            key={entry.id}
                                            draggableId={entry.id.toString()}
                                            index={entryIndex}
                                            isDragDisabled={!!searchText}
                                          >
                                            {(provided) => (
                                              <li
                                                //entry
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={`directory-info row padding-top border-top ${
                                                  entryIndex === category.items.length - 1
                                                    ? 'border-bottom padding-bottom'
                                                    : ''
                                                } ${
                                                  draggables.entry.selected === entry.id
                                                    ? 'selected'
                                                    : ''
                                                } ${
                                                  draggables.entry.held === entry.id ? 'held' : ''
                                                } ${searchText ? 'dragDisabled' : ''}`}
                                                onMouseDown={() =>
                                                  setDraggables({
                                                    ...draggables,
                                                    entry: { ...draggables.entry, held: entry.id },
                                                  })
                                                }
                                                onMouseUp={() =>
                                                  setDraggables({
                                                    ...draggables,
                                                    entry: { ...draggables.entry, held: '' },
                                                  })
                                                }
                                              >
                                                {sectionComponent1(entry)}
                                                {sectionComponent2(entry)}
                                                {sectionComponent3(entry)}
                                                <div className="col-xs-2 text-right no-right-padding pull-right">
                                                  <button
                                                    onClick={() =>
                                                      handleEditEntry(entry.categoryId, entry.id)
                                                    }
                                                    type="button"
                                                    className="btn"
                                                    style={{ marginRight: '6px' }}
                                                  >
                                                    Edit
                                                  </button>
                                                  <button
                                                    onClick={() =>
                                                      localEntryArraysByCategory.length
                                                        ? handleAddEntry()
                                                        : null
                                                    }
                                                    type="button"
                                                    className="btn"
                                                  >
                                                    Copy
                                                  </button>
                                                </div>
                                              </li>
                                            )}
                                          </Draggable>
                                        ))}
                                      {provided.placeholder}
                                    </ul>
                                  )}
                                </Droppable>
                              </div>
                            </div>
                          </li>
                        ))}
                        {provided.placeholder}
                      </ul>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectoryList;
